import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FileCheck, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, XCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, LayoutGrid, Layers, Clock, User, FileText, TrendingUp, List, LayoutList, Shield } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageFormTabs,
  pointageFormTabVisible,
  usePointageEmployeeOptions,
  resolvePointageEmployeeFromSelect,
  resolvePointageEmployeeDisplayName,
  resolvePointageEmployeeSelectValue,
} from './pointageListPageUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatPointageDate, formatPointageTime, formatPointageShortDate, JUSTIFICATION_TYPE_VALUES, JUSTIFICATION_STATUS_VALUES, normalizeJustificationType, normalizeJustificationStatus } from './pointageHelpers';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

const JustificatifsPage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.pointage.pages.justificatifsPage.${key}`, opts);

  const getStatusLabel = useCallback((status) => {
    const normalized = normalizeJustificationStatus(status);
    const labels = {
      [JUSTIFICATION_STATUS_VALUES.pending]: p('status_pending'),
      [JUSTIFICATION_STATUS_VALUES.validated]: p('status_validated'),
      [JUSTIFICATION_STATUS_VALUES.rejected]: p('status_rejected'),
    };
    return labels[normalized] || p('em_dash');
  }, [t]);

  const getTypeLabel = useCallback((type) => {
    const normalized = normalizeJustificationType(type);
    const labels = {
      [JUSTIFICATION_TYPE_VALUES.medical]: p('type_medical_certificate'),
      [JUSTIFICATION_TYPE_VALUES.death]: p('type_death_certificate'),
      [JUSTIFICATION_TYPE_VALUES.marriage]: p('type_marriage_certificate'),
      [JUSTIFICATION_TYPE_VALUES.emergency]: p('type_emergency'),
      [JUSTIFICATION_TYPE_VALUES.hajjTravel]: p('type_hajj_travel'),
      [JUSTIFICATION_TYPE_VALUES.court]: p('type_court_summons'),
      [JUSTIFICATION_TYPE_VALUES.late]: p('type_late_justification'),
      [JUSTIFICATION_TYPE_VALUES.absence]: p('type_absence_authorization'),
      [JUSTIFICATION_TYPE_VALUES.other]: p('type_other'),
    };
    return labels[normalized] || type;
  }, [t]);

  const justificationTypeOptions = [
    { value: JUSTIFICATION_TYPE_VALUES.medical, label: p('type_medical_certificate') },
    { value: JUSTIFICATION_TYPE_VALUES.death, label: p('type_death_certificate') },
    { value: JUSTIFICATION_TYPE_VALUES.marriage, label: p('type_marriage_certificate') },
    { value: JUSTIFICATION_TYPE_VALUES.emergency, label: p('type_emergency') },
    { value: JUSTIFICATION_TYPE_VALUES.hajjTravel, label: p('type_hajj_travel') },
    { value: JUSTIFICATION_TYPE_VALUES.court, label: p('type_court_summons') },
    { value: JUSTIFICATION_TYPE_VALUES.late, label: p('type_late_justification') },
    { value: JUSTIFICATION_TYPE_VALUES.absence, label: p('type_absence_authorization') },
    { value: JUSTIFICATION_TYPE_VALUES.other, label: p('type_other') },
  ];

  const [searchTerm, setSearchTerm] = useState('');
  const [justificatifs, setJustificatifs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const { employeeOptions } = usePointageEmployeeOptions(
    localApiAdapter.get.bind(localApiAdapter),
    { valueField: 'id' },
  );

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);
  
  const [formTab, setFormTab] = useState('general');

  const JUSTIFICATIF_FORM_FIELD_COUNT = 13;

  const justificatifFormTabs = useMemo(() => buildPointageFormTabs({
    two: [
      { id: 'general', label: p('section_employee_info'), icon: User, iconClass: 'text-emerald-400' },
      { id: 'details', label: p('section_document_validation'), icon: CheckCircle, iconClass: 'text-green-400' },
    ],
  }, JUSTIFICATIF_FORM_FIELD_COUNT), [p]);
  
  const [formData, setFormData] = useState({ 
    submission_date: '',
    employee_id: '',
    employee_name: '',
    justification_type: '',
    reason: '',
    period_start: '',
    period_end: '',
    document_name: '',
    document_url: '',
    status: '',
    validator_name: '',
    validation_date: '',
    validator_comment: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_justificatifs');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadJustificatifs(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadJustificatifs = async () => {
    try {
      const response = await localApiAdapter.get('/pointage/justificatifs');
      const rows = response?.data || response || [];
      setJustificatifs(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading justificatifs:', error);
      setJustificatifs([]);
    }
  };

  const stats = {
    total: justificatifs.length,
    enAttente: justificatifs.filter(j => normalizeJustificationStatus(j.status) === JUSTIFICATION_STATUS_VALUES.pending).length,
    valides: justificatifs.filter(j => normalizeJustificationStatus(j.status) === JUSTIFICATION_STATUS_VALUES.validated).length,
    rejetes: justificatifs.filter(j => normalizeJustificationStatus(j.status) === JUSTIFICATION_STATUS_VALUES.rejected).length,
    medicaux: justificatifs.filter(j => normalizeJustificationType(j.justification_type) === JUSTIFICATION_TYPE_VALUES.medical).length
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_justificatifs', name: p('mainTab_general'), icon: LayoutGrid },
    { id: 'par_type', name: p('mainTab_by_type'), icon: FileText },
    { id: 'par_statut', name: p('mainTab_by_status'), icon: Shield }
  ]), [t]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('quickFilter_all'), icon: Layers },
    { id: JUSTIFICATION_STATUS_VALUES.pending, name: p('quickFilter_pending'), icon: Clock },
    { id: JUSTIFICATION_STATUS_VALUES.validated, name: p('quickFilter_validated'), icon: CheckCircle },
    { id: JUSTIFICATION_STATUS_VALUES.rejected, name: p('quickFilter_rejected'), icon: XCircle },
    { id: JUSTIFICATION_TYPE_VALUES.medical, name: p('quickFilter_medical'), icon: AlertTriangle }
  ]), [t]);

  const getTypeDistribution = () => {
    const counts = justificationTypeOptions.map((opt) => ({
      filterKey: opt.value,
      name: opt.label,
      count: justificatifs.filter(j => normalizeJustificationType(j.justification_type) === opt.value).length,
    }));
    const colors = [
      "bg-gradient-to-r from-red-500 to-red-600",
      "bg-gradient-to-r from-gray-500 to-gray-600",
      "bg-gradient-to-r from-pink-500 to-pink-600",
      "bg-gradient-to-r from-blue-500 to-blue-600",
      "bg-gradient-to-r from-purple-500 to-purple-600",
      "bg-gradient-to-r from-orange-500 to-orange-600",
      "bg-gradient-to-r from-yellow-500 to-yellow-600",
      "bg-gradient-to-r from-cyan-500 to-cyan-600",
      "bg-gradient-to-r from-slate-500 to-slate-600",
    ];
    return counts.map((item, idx) => ({ ...item, color: colors[idx % colors.length] }));
  };

  const getStatutDistribution = () => {
    return [
      { filterKey: JUSTIFICATION_STATUS_VALUES.pending, name: p('dist_pending'), count: stats.enAttente, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
      { filterKey: JUSTIFICATION_STATUS_VALUES.validated, name: p('dist_validated'), count: stats.valides, color: "bg-gradient-to-r from-green-500 to-green-600" },
      { filterKey: JUSTIFICATION_STATUS_VALUES.rejected, name: p('dist_rejected'), count: stats.rejetes, color: "bg-gradient-to-r from-red-500 to-red-600" }
    ];
  };

  const justificatifDistribution = useMemo(() => ({
    all_justificatifs: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: JUSTIFICATION_STATUS_VALUES.pending, name: p('dist_pending'), count: stats.enAttente, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
        { filterKey: JUSTIFICATION_STATUS_VALUES.validated, name: p('dist_validated'), count: stats.valides, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: JUSTIFICATION_STATUS_VALUES.rejected, name: p('dist_rejected'), count: stats.rejetes, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterKey: JUSTIFICATION_TYPE_VALUES.medical, name: p('dist_medical'), count: stats.medicaux, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: getTypeDistribution()
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: getStatutDistribution()
    }
  }), [t, justificatifs, stats.enAttente, stats.valides, stats.rejetes, stats.medicaux]);

  const distributionInfo = justificatifDistribution[mainViewMode] || justificatifDistribution.all_justificatifs;

  const baseFilteredJustificatifs = useMemo(() => justificatifs.filter(j => {
    if (statusFilter !== 'all' && j.status !== statusFilter) return false;

    if (dateRange.start || dateRange.end) {
      const submissionDate = new Date(j.submission_date);

      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (submissionDate < startOfDay) return false;
      }

      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (submissionDate > endOfDay) return false;
      }
    }

    if (searchTerm && !j.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !j.justification_type?.toLowerCase().includes(searchTerm.toLowerCase()) && !j.validator_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;

    if (quickFilter !== 'all') {
      if ([JUSTIFICATION_STATUS_VALUES.pending, JUSTIFICATION_STATUS_VALUES.validated, JUSTIFICATION_STATUS_VALUES.rejected].includes(quickFilter)) {
        if (normalizeJustificationStatus(j.status) !== quickFilter) return false;
      } else if (normalizeJustificationType(j.justification_type) !== quickFilter) return false;
    }

    if (activeDistributionFilter) {
      if (mainViewMode === 'par_statut' || mainViewMode === 'all_justificatifs') {
        if (activeDistributionFilter === JUSTIFICATION_TYPE_VALUES.medical) {
          if (normalizeJustificationType(j.justification_type) !== JUSTIFICATION_TYPE_VALUES.medical) return false;
        } else if (normalizeJustificationStatus(j.status) !== activeDistributionFilter) return false;
      }
      if (mainViewMode === 'par_type') {
        if (normalizeJustificationType(j.justification_type) !== activeDistributionFilter) return false;
      }
    }
    return true;
  }), [justificatifs, statusFilter, dateRange, searchTerm, quickFilter, activeDistributionFilter, mainViewMode]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('submission_date', (j) => formatPointageDate(j.submission_date) || ''),
      textColumnRule('employee_name', (j) => j.employee_name || ''),
      textColumnRule('justification_type', (j) => getTypeLabel(j.justification_type)),
      textColumnRule('period', (j) => `${j.period_start || ''} ${j.period_end || ''}`.trim()),
      textColumnRule('document', (j) => j.document_name || j.document || ''),
      textColumnRule('validator', (j) => j.validator_name || ''),
      exactColumnRule('status', (j) => normalizeJustificationStatus(j.status)),
    ],
    [getStatusLabel, getTypeLabel],
  );

  const filteredJustificatifs = useMemo(
    () => applyColumnFilters(baseFilteredJustificatifs, colFilters, columnFilterRules),
    [baseFilteredJustificatifs, colFilters, columnFilterRules],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        employee_name: formData.employee_name
          || resolvePointageEmployeeDisplayName(formData.employee_id, employeeOptions, ''),
      };
      if (editingRecord) {
        await localApiAdapter.put(`/pointage/justificatifs/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/pointage/justificatifs', submitData);
        notifyCreated(showNotify, p('title'));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error')); return;
    } finally {
      resetForm();
      loadJustificatifs();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/pointage/justificatifs/${id}`);
        notifyDeleted(showNotify, p('title'));
        loadJustificatifs();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/pointage/justificatifs/${id}`, { status: newStatus });
      loadJustificatifs();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const clickableStatusOptions = useMemo(() => [
    { value: JUSTIFICATION_STATUS_VALUES.pending, label: p('status_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: JUSTIFICATION_STATUS_VALUES.validated, label: p('status_validated'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: JUSTIFICATION_STATUS_VALUES.rejected, label: p('status_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ], [p]);

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/pointage/justificatifs/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        setSelectedRecords([]);
        loadJustificatifs();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      submission_date: '',
      employee_id: '',
      employee_name: '',
      justification_type: '',
      reason: '',
      period_start: '',
      period_end: '',
      document_name: '',
      document_url: '',
      status: '',
      validator_name: '',
      validation_date: '',
      validator_comment: '',
      notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredJustificatifs);


  const POINTAGE_TABLE_COLUMNS = [
    { key: 'submission_date', labelKey: 'col_submission_date' },
    { key: 'employee_name', labelKey: 'col_employee' },
    { key: 'justification_type', labelKey: 'col_type' },
    { key: 'period', labelKey: 'col_period' },
    { key: 'document', labelKey: 'col_document' },
    { key: 'validator', labelKey: 'col_validator' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'col_actions', required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FileCheck className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadJustificatifs} className="refresh-button" title={t('common.refresh')}><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          {buildPointageBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, label: `${t('common.delete')} (${selectedRecords.length})`, title: t('common.delete', { defaultValue: 'حذف' }) })}
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={() => { setEditingRecord(null); setFormTab('general'); setShowModal(true); }}>
            <Plus className="h-5 w-5" /><span>{p('new_record')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: FileCheck, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_pending'), value: stats.enAttente, icon: Clock, color: 'from-yellow-500 to-yellow-600' },
          { title: p('stat_validated'), value: stats.valides, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_rejected'), value: stats.rejetes, icon: XCircle, color: 'from-red-500 to-red-600' },
          { title: p('stat_medical'), value: stats.medicaux, icon: AlertTriangle, color: 'from-red-500 to-red-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {quickFilterButtons.map((filter) => (
                <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                </button>
              ))}
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.filterKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })()}{p('percent_of_total')}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })} deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })} />
                  {isColVisible('submission_date') && (
                    <TableColumnHeaderFilter
                      columnKey="submission_date"
                      label={p('col_submission_date')}
                      value={colFilters.submission_date ?? ''}
                      onChange={(v) => setColFilter('submission_date', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white"
                    />
                  )}
                  {isColVisible('employee_name') && (
                    <TableColumnHeaderFilter
                      columnKey="employee_name"
                      label={p('col_employee')}
                      value={colFilters.employee_name ?? ''}
                      onChange={(v) => setColFilter('employee_name', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white"
                    />
                  )}
                  {isColVisible('justification_type') && (
                    <TableColumnHeaderFilter
                      columnKey="justification_type"
                      label={p('col_type')}
                      value={colFilters.justification_type ?? ''}
                      onChange={(v) => setColFilter('justification_type', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white"
                    />
                  )}
                  {isColVisible('period') && (
                    <TableColumnHeaderFilter columnKey="period" label={p('col_period')} value={colFilters.period ?? ''} onChange={(v) => setColFilter('period', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('document') && (
                    <TableColumnHeaderFilter columnKey="document" label={p('col_document')} value={colFilters.document ?? ''} onChange={(v) => setColFilter('document', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('validator') && (
                    <TableColumnHeaderFilter columnKey="validator" label={p('col_validator')} value={colFilters.validator ?? ''} onChange={(v) => setColFilter('validator', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter
                      columnKey="status"
                      label={p('col_status')}
                      kind="select"
                      options={statusColumnFilterOptions}
                      value={colFilters.status ?? ''}
                      onChange={(v) => setColFilter('status', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      align="center"
                      thClassName="text-white"
                    />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredJustificatifs.length > 0 ? (
                  filteredJustificatifs.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {isColVisible('submission_date') && (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Clock size={14} className="text-white/50" />
                              {record.submission_date ? formatPointageDate(record.submission_date) : '-'}
                            </div>
                            <span className="text-white/50 text-xs mt-0.5">
                              {record.submission_date ? formatPointageTime(record.submission_date) : ''}
                            </span>
                          </div>
                        </td>
                      )}
                      {isColVisible('employee_name') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{resolvePointageEmployeeDisplayName(record.employee_id, employeeOptions, record.employee_name || '-')}</span></td>
                      )}
                      {isColVisible('justification_type') && (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{getTypeLabel(record.justification_type) || '-'}</span>
                            {record.reason && <span className="text-white/50 text-xs mt-0.5 line-clamp-1">{record.reason}</span>}
                          </div>
                        </td>
                      )}
                      {isColVisible('period') && (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold text-sm">
                              {record.period_start ? formatPointageShortDate(record.period_start) : '-'}
                              {record.period_end && ` - ${formatPointageShortDate(record.period_end)}`}
                            </span>
                          </div>
                        </td>
                      )}
                      {isColVisible('document') && (
                        <td className="px-6 py-4">
                          <span className="text-white/70 text-sm">{record.document_name || '-'}</span>
                        </td>
                      )}
                      {isColVisible('validator') && (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold text-sm">{record.validator_name || '-'}</span>
                            {record.validation_date && <span className="text-white/50 text-xs mt-0.5">{formatPointageDate(record.validation_date)}</span>}
                          </div>
                        </td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={normalizeJustificationStatus(record.status)}
                              recordId={record.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><FileCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredJustificatifs.length })}</span>
                      <span>{p('footer_pending')} <span className="text-white font-semibold">{filteredJustificatifs.filter(j => normalizeJustificationStatus(j.status) === JUSTIFICATION_STATUS_VALUES.pending).length}</span></span>
                      <span>{p('footer_validated')} <span className="text-white font-semibold">{filteredJustificatifs.filter(j => normalizeJustificationStatus(j.status) === JUSTIFICATION_STATUS_VALUES.validated).length}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredJustificatifs.length > 0 ? (
              filteredJustificatifs.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><FileCheck size={20} className="text-emerald-400" />{record.employee_name || p('not_available')}</h3>
                        <div className="flex">{getStatusBadge(record.status)}</div>
                        {record.submission_date && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                            {formatPointageDate(record.submission_date)}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_employee_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}</span> {record.employee_name || '-'}</div>
                            <div><span className="font-medium">{p('label_submission_date')}</span> {record.submission_date ? formatPointageDate(record.submission_date) : '-'}</div>
                            <div><span className="font-medium">{p('col_status')}:</span> {getStatusBadge(record.status)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_type_reason')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_type')}</span> {getTypeLabel(record.justification_type) || '-'}</div>
                            {record.reason && (
                              <div className="pt-2 border-t border-white/20">
                                <span className="font-medium">{p('label_reason')}</span>
                                <p className="text-white/60 text-xs mt-1">{record.reason}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_period')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_start')}</span> {record.period_start ? formatPointageDate(record.period_start) : '-'}</div>
                            <div><span className="font-medium">{p('label_end')}</span> {record.period_end ? formatPointageDate(record.period_end) : '-'}</div>
                            <div><span className="font-medium">{p('label_document')}</span> {record.document_name || '-'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_validation')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_validator')}</span> {record.validator_name || '-'}</div>
                            <div><span className="font-medium">{p('label_validation_date')}</span> {record.validation_date ? formatPointageDate(record.validation_date) : '-'}</div>
                            {record.validator_comment && (
                              <div className="pt-2 border-t border-white/20">
                                <span className="font-medium">{p('label_comment')}</span>
                                <p className="text-white/60 text-xs mt-1">{record.validator_comment}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><FileCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="1100px"
          testId="justificatifs-view-modal"
        >
          <div className="space-y-4">
                <div className={modalForm.viewCard}>
                  <h3 className={`${modalForm.viewHeading} flex items-center gap-2`}><User size={18} className="text-emerald-400" />{p('section_employee_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className={modalForm.viewLabel}>{p('label_employee_name')}</p><p className={modalForm.viewValue}>{viewingRecord.employee_name}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('label_submission_date_full')}</p><p className={modalForm.viewValue}>{viewingRecord.submission_date ? formatPointageDate(viewingRecord.submission_date) : '-'}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('col_status')}</p>{getStatusBadge(viewingRecord.status)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_type_reason')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('col_type')}</p><p className={modalForm.viewValue}>{getTypeLabel(viewingRecord.justification_type) || '-'}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('label_reason_full')}</p><p className={modalForm.viewValue}>{viewingRecord.reason || '-'}</p></div>
                    </div>
                  </div>
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_period')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('label_period_start')}</p><p className={modalForm.viewValue}>{viewingRecord.period_start ? formatPointageDate(viewingRecord.period_start) : '-'}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('label_period_end')}</p><p className={modalForm.viewValue}>{viewingRecord.period_end ? formatPointageDate(viewingRecord.period_end) : '-'}</p></div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_document')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('label_document_name')}</p><p className={modalForm.viewValue}>{viewingRecord.document_name || '-'}</p></div>
                      {viewingRecord.document_url && <div><p className={modalForm.viewLabel}>{p('label_url')}</p><a href={viewingRecord.document_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm underline">{viewingRecord.document_url}</a></div>}
                    </div>
                  </div>
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_validation')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('col_validator')}</p><p className={modalForm.viewValue}>{viewingRecord.validator_name || '-'}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('label_validation_date_full')}</p><p className={modalForm.viewValue}>{viewingRecord.validation_date ? formatPointageDate(viewingRecord.validation_date) : '-'}</p></div>
                      {viewingRecord.validator_comment && <div><p className={modalForm.viewLabel}>{p('label_comment')}</p><p className="text-white/70 text-sm">{viewingRecord.validator_comment}</p></div>}
                    </div>
                  </div>
                </div>
                {viewingRecord.notes && (
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_notes')}</h3>
                    <p className="text-white/70">{viewingRecord.notes}</p>
                  </div>
                )}
              </div>
        </QawaemErpViewModal>
      )}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_edit') : p('modal_new')}
          icon={FileText}
          onSubmit
          formId="justificatif-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          maxWidth="1200px"
          testId="justificatifs-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={justificatifFormTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            formId="justificatif-form"
            onSubmit={handleSubmit}
            ariaLabel={editingRecord ? p('modal_edit') : p('modal_new')}
          >
            {pointageFormTabVisible(justificatifFormTabs, formTab, 'general') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('label_employee_name_required')} />}</label><CustomSelect value={resolvePointageEmployeeSelectValue(formData, employeeOptions)} onChange={(value) => { const { employeeId, employeeName } = resolvePointageEmployeeFromSelect(value, employeeOptions); setFormData({ ...formData, employee_id: employeeId, employee_name: employeeName }); }} options={employeeOptions} placeholder={p('placeholder_full_name')} searchable={true} creatable={false} autoSelect={false} /></div>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('label_submission_date_required')} />}</label><CustomDatePicker value={formData.submission_date} onChange={(val) => setFormData({ ...formData, submission_date: val })} placeholder={p('select')} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div className="md:col-span-3"><label className={modalForm.label}>{<RequiredLabelText text={p('label_justification_type_required')} />}</label><CustomSelect value={formData.justification_type} onChange={(value) => setFormData({ ...formData, justification_type: value })} options={justificationTypeOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('placeholder_choose_or_type')} /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('label_reason_full')}</label><textarea value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_reason_details')} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('label_date_start')}</label><CustomDatePicker value={formData.period_start} onChange={(val) => setFormData({ ...formData, period_start: val })} placeholder={p('select')} /></div>
                <div><label className={modalForm.label}>{p('label_date_end')}</label><CustomDatePicker value={formData.period_end} onChange={(val) => setFormData({ ...formData, period_end: val })} placeholder={p('select')} minDate={formData.period_start ? new Date(formData.period_start) : undefined} /></div>
              </div>
              </div>
            )}
            {pointageFormTabVisible(justificatifFormTabs, formTab, 'details') && (
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('label_document_name')}</label><input type="text" value={formData.document_name} onChange={(e) => setFormData({ ...formData, document_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_certificat')} /></div>
                <div><label className={modalForm.label}>{p('label_document_url')}</label><input type="text" value={formData.document_url} onChange={(e) => setFormData({ ...formData, document_url: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_url')} /></div>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('label_status_required')} />}</label><CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions.map(s => ({ value: s.value, label: s.label }))} searchable={true} creatable={true} autoSelect={false} placeholder={p('placeholder_choose')} /></div>
                <div><label className={modalForm.label}>{p('col_validator')}</label><input type="text" value={formData.validator_name} onChange={(e) => setFormData({ ...formData, validator_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_validator_name')} /></div>
                <div><label className={modalForm.label}>{p('label_validation_date_full')}</label><CustomDatePicker value={formData.validation_date} onChange={(val) => setFormData({ ...formData, validation_date: val })} placeholder={p('select')} /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('label_validator_comment')}</label><textarea value={formData.validator_comment} onChange={(e) => setFormData({ ...formData, validator_comment: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_comment')} /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('section_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_notes')} /></div>
              </div>
            )}
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default JustificatifsPage;

